import { useCloudFileAttachmentUpload } from '#pplx/frontend/apps/ask/composer-sdk-plugins/file-attachments/internal/useCloudFileAttachmentUpload.js';
import { useFileAttachmentUpload } from '#pplx/frontend/apps/ask/composer-sdk-plugins/file-attachments/internal/useFileAttachmentUpload.js';
import { filesFromDropItems } from '#pplx/frontend/apps/ask/composer-sdk-plugins/file-upload/dropFiles.js';
import { safeRandomUUID } from '#pplx/frontend/common/safeRandomUUID.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { fileExtension, useTrackedFileUploadAnalytics, } from '#pplx/frontend/components/hooks/fileUploadAnalytics.js';
import { useUploadFiles, validateFile } from '#pplx/frontend/components/hooks/useUploadFiles.js';
import { ATTACHMENT_FAILURE_MESSAGES } from '#pplx/frontend/components/utils/attachmentsUtils.js';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
export function useFileAttachmentIngress({ disabled, files, gateUploadFile, onUploadAttempted, reason, removedIdsRef, setError, setFiles, setAttachmentMetadataById, uploadPolicy, }) {
    const { $t } = useIntl();
    const { started, completed } = useTrackedFileUploadAnalytics('thread_attachment');
    // TODO(https://github.com/ppl-ai/agi/pull/173727#discussion_r3617429014): Redact filenames
    // from useUploadFiles image-resize telemetry for every attachment surface.
    const { rejectFiles, uploadFiles } = useUploadFiles({
        analyticsSurface: 'thread_attachment',
        defaultFileSource: uploadPolicy.transport.defaultFileSource,
        maxFileSizeMB: uploadPolicy.constraints.maxFileSizeMB,
        onRateLimitUpdate: uploadPolicy.quota.consumeUploadCount,
        reason,
        skipParsing: uploadPolicy.transport.skipParsing,
        uploadRateLimit: uploadPolicy.quota.remainingUploadCount,
    });
    const cloudTransport = useCloudFileAttachmentUpload({ reason, removedIdsRef, uploadPolicy });
    const uploadLocalFiles = useCallback((selectedFiles, onChange, signal) => {
        const validFiles = [];
        for (const file of selectedFiles) {
            const error = validateFile({
                file,
                maxFileSizeMB: uploadPolicy.constraints.maxFileSizeMB,
                skipParsing: uploadPolicy.transport.skipParsing,
            });
            if (error === null) {
                validFiles.push(file);
                continue;
            }
            let message;
            switch (error.code) {
                case 'unsupportedType':
                    message = $t(ATTACHMENT_FAILURE_MESSAGES.unsupported_type);
                    break;
                case 'tooLarge':
                    message = $t(ATTACHMENT_FAILURE_MESSAGES.too_large, { max: `${error.max}MB` });
                    break;
                case 'tooSmall':
                    message = $t(ATTACHMENT_FAILURE_MESSAGES.too_small);
                    break;
                case 'noName':
                    message = $t(ATTACHMENT_FAILURE_MESSAGES.no_name);
                    break;
                default:
                    assertUnreachableValue(error);
            }
            setError({ type: 'selection', message });
            // Rejected files still complete an upload attempt without entering the tray.
            const attempt = {
                uploadAttemptUUID: safeRandomUUID(),
                fileUUID: null,
                sourceType: 'local',
                fileSizeBytes: file.size,
                fileExtension: fileExtension(file.name),
                fileCount: selectedFiles.length,
            };
            started(attempt);
            completed(attempt, 'failure', error.code === 'tooLarge' ? 'file_too_large' : 'unsupported_type');
        }
        if (validFiles.length === 0)
            return Promise.resolve();
        return uploadFiles(validFiles, (changedFiles) => onChange?.(changedFiles.map((file) => ({
            ...file,
            previewTarget: { kind: 'local', file: file.file },
        }))), signal);
    }, [
        $t,
        completed,
        setError,
        started,
        uploadFiles,
        uploadPolicy.constraints.maxFileSizeMB,
        uploadPolicy.transport.skipParsing,
    ]);
    const activeControllersRef = useRef(new Set());
    const disabledRef = useRef(disabled);
    const dropGenerationRef = useRef(0);
    disabledRef.current = disabled;
    useEffect(() => {
        const activeControllers = activeControllersRef.current;
        return () => {
            dropGenerationRef.current += 1;
            activeControllers.forEach((controller) => controller.abort());
        };
    }, []);
    useEffect(() => {
        if (disabled)
            dropGenerationRef.current += 1;
    }, [disabled]);
    const onFilesSelected = useFileAttachmentUpload({
        activeControllersRef,
        disabled,
        files,
        gateUploadFile,
        onUploadAttempted,
        rejectFiles,
        removedIdsRef,
        setError,
        setFiles,
        setAttachmentMetadataById,
        uploadFiles: uploadLocalFiles,
        uploadPolicy,
    });
    // The cloud transport records provider-specific rejections; suppress the default local event.
    const gateCloudUpload = useCallback(() => gateUploadFile('other_connectors', { trackRejection: false }), [gateUploadFile]);
    const onCloudFilesSelected = useFileAttachmentUpload({
        activeControllersRef,
        disabled,
        files,
        gateUploadFile: gateCloudUpload,
        onUploadAttempted,
        ...cloudTransport,
        removedIdsRef,
        setError,
        setFiles,
        setAttachmentMetadataById,
        uploadPolicy,
    });
    const onCloudFilesSelectedRef = useRef(onCloudFilesSelected);
    onCloudFilesSelectedRef.current = onCloudFilesSelected;
    // Pickers can outlive their opening menu. Recheck current policy on delivery.
    const uploadCloudFiles = useCallback((documents) => {
        onCloudFilesSelectedRef.current(documents, 'cloud');
    }, []);
    const onFilesSelectedRef = useRef(onFilesSelected);
    onFilesSelectedRef.current = onFilesSelected;
    const uploadFileAsAttachment = useCallback((file, metadata) => {
        onFilesSelectedRef.current([file], 'imperative', metadata);
    }, []);
    const onDropItems = useCallback(async (items) => {
        if (disabled)
            return;
        const dropGeneration = dropGenerationRef.current;
        const droppedFiles = await filesFromDropItems(items, Math.max(0, uploadPolicy.constraints.maxFiles - files.length));
        if (disabledRef.current || dropGeneration !== dropGenerationRef.current)
            return;
        onFilesSelectedRef.current(droppedFiles, 'drop');
    }, [disabled, files.length, uploadPolicy.constraints.maxFiles]);
    return useMemo(() => ({ uploadCloudFiles, uploadFileAsAttachment, onDropItems, onFilesSelected }), [uploadCloudFiles, uploadFileAsAttachment, onDropItems, onFilesSelected]);
}
//# sourceMappingURL=useFileAttachmentIngress.js.map